import { useState, useCallback } from 'react';
import type { Amenity } from '@wineryxplorer/shared';
import { ValidationError } from '../ui/ValidationError';
import { LoadingSpinner } from '../ui/LoadingSpinner';

interface AmenitiesSelectorProps {
  value: Amenity[];
  onChange: (value: Amenity[]) => void;
  error?: string | string[];
  disabled?: boolean;
  loading?: boolean;
  required?: boolean;
  maxSelections?: number;
}

const AMENITY_LABELS = {
  'scenic-views': 'Scenic Views',
  'historic-building': 'Historic Building',
  'family-friendly': 'Family-Friendly',
  'pet-friendly': 'Pet-Friendly',
  'outdoor-seating': 'Outdoor Seating',
  'live-music': 'Live Music',
  'food-service': 'Food Service',
  'tours-available': 'Tours Available',
  'event-space': 'Event Space',
  'wheelchair-accessible': 'Wheelchair Accessible',
} as const;

const VALID_AMENITIES: readonly string[] = [
  'scenic-views',
  'historic-building',
  'family-friendly',
  'pet-friendly',
  'outdoor-seating',
  'live-music',
  'food-service',
  'tours-available',
  'event-space',
  'wheelchair-accessible',
];

export const AmenitiesSelector = ({ 
  value = [], 
  onChange, 
  error, 
  disabled = false, 
  loading = false,
  required = false,
  maxSelections = 10
}: AmenitiesSelectorProps) => {
  const [localError, setLocalError] = useState<string>('');

  const handleToggle = useCallback((amenity: Amenity) => {
    if (disabled || loading) return;
    
    setLocalError(''); // Clear local validation error
    
    // Client-side validation
    if (!VALID_AMENITIES.includes(amenity)) {
      setLocalError(`Invalid amenity. Valid options are: ${VALID_AMENITIES.join(', ')}`);
      return;
    }

    const newValue = value.includes(amenity)
      ? value.filter((a) => a !== amenity)
      : [...value, amenity];

    // Check for duplicates (shouldn't happen with this logic, but good to validate)
    const uniqueAmenities = Array.from(new Set(newValue));
    if (uniqueAmenities.length !== newValue.length) {
      setLocalError('Duplicate amenities are not allowed');
      return;
    }

    // Check max selections
    if (newValue.length > maxSelections) {
      setLocalError(`Maximum ${maxSelections} amenities allowed`);
      return;
    }

    onChange(newValue);
  }, [value, onChange, disabled, loading, maxSelections]);

  const displayError = error || localError;
  const hasError = Boolean(displayError);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Amenities
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        
        {loading && (
          <LoadingSpinner size="sm" className="text-gray-400" />
        )}
        
        <div className="text-sm text-gray-500">
          {value.length}/{maxSelections} selected
        </div>
      </div>
      
      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${
        disabled || loading ? 'opacity-50 pointer-events-none' : ''
      }`}>
        {Object.entries(AMENITY_LABELS).map(([key, label]) => {
          const isSelected = value.includes(key as Amenity);
          const isDisabledOption = disabled || loading || 
            (!isSelected && value.length >= maxSelections);
          
          return (
            <label 
              key={key} 
              className={`flex items-center space-x-3 cursor-pointer p-3 rounded-lg transition-colors touch-manipulation ${
                isDisabledOption 
                  ? 'cursor-not-allowed opacity-50' 
                  : 'hover:bg-gray-50'
              } ${
                hasError ? 'border border-red-200' : 'border border-transparent'
              }`}
              style={{ minHeight: '44px' }} // Ensure minimum touch target size
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleToggle(key as Amenity)}
                disabled={isDisabledOption}
                className={`h-5 w-5 text-primary-600 focus:ring-2 border-gray-300 rounded touch-manipulation ${
                  hasError 
                    ? 'focus:ring-red-500 border-red-300' 
                    : 'focus:ring-info-500'
                }`}
                aria-describedby={`${key}-description`}
              />
              <span 
                className="text-base text-gray-700 select-none" 
                id={`${key}-description`}
              >
                {label}
              </span>
            </label>
          );
        })}
      </div>
      
      {required && value.length === 0 && (
        <ValidationError 
          error="At least one amenity must be selected" 
          className="mt-1"
        />
      )}
      
      <ValidationError 
        error={displayError} 
        className="mt-1"
      />
    </div>
  );
};